import { PaymentMethod } from 'store/api/payment-methods';
import { Payout, PayrollPayoutItemRequest } from 'store/api/payout';
import { Paychecks, Adjustment, PayoutType, PaychecksTitle } from 'types';

type KeysPaycheck = keyof Omit<
  Paychecks,
  'event' | 'payoutItems' | 'refundAdjustments' | 'chargebackAdjustments'
>;

export const getTransformedPaychecks = (paychecks: Paychecks) => {
  const order: KeysPaycheck[] = [
    'grossSalesTotal',
    'netSalesTotal',
    'tipTotal',
    'commissionsTotal',
    'bonusesTotal',
    'reimbursementsTotal',
    'miscPositiveTotal',
    'totalPay',
    'chargebackTotal',
    'refundTotal',
    'advancesTotal',
    'freeShippingTotal',
    'brokenMissingTotal',
    'underMinimumTotal',
    'miscNegativeTotal',
    'totalPayout',
  ];

  return order
    .filter((key) => {
      if (key === 'totalPay' || key === 'totalPayout') {
        return true;
      }
      if (
        key === 'chargebackTotal' &&
        buildDisplayChargebackAdjustments(paychecks.chargebackAdjustments)
          .length
      ) {
        return true;
      }
      if (key === 'refundTotal' && paychecks.refundAdjustments?.length) {
        return true;
      }
      return Math.round(Math.abs(Number(paychecks[key]) || 0) * 100) > 0;
    })
    .map((key) => ({
      key,
      value: paychecks[key],
      name: PaychecksTitle[key],
    }));
};

export interface DisplayChargebackAdjustment {
  key: string;
  label: string;
  amount: number;
  checkoutId?: number | string;
}

type ChargebackAdjustmentGroup = {
  key: string;
  positiveTotal: number;
  negativeTotal: number;
  sortValue: number | string;
  positiveRows: Adjustment[];
  negativeRows: Adjustment[];
  checkoutId?: number | string;
};

type SortableDisplayChargebackAdjustment = DisplayChargebackAdjustment & {
  sortValue: number | string;
};

const parseCheckoutIdFromLabel = (label?: string) => {
  return label?.match(/Chargeback #([^\s]+)/)?.[1];
};

const getChargebackGroupKey = (adjustment: Adjustment, index: number) => {
  const checkoutId =
    adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label);

  return checkoutId ? `checkout-${checkoutId}` : `unmatched-${index}`;
};

const getChargebackSortValue = (adjustment: Adjustment) => {
  const checkoutId =
    adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label);

  if (!checkoutId) {
    return adjustment.label ?? '';
  }

  const numericId = Number(checkoutId);

  return Number.isNaN(numericId) ? String(checkoutId) : numericId;
};

const getWonLabel = (group: ChargebackAdjustmentGroup) => {
  const wonRow =
    group.positiveRows.find((row) => row.label?.includes('Won')) ??
    group.positiveRows[0];

  if (wonRow?.label) {
    return wonRow.label;
  }

  return group.checkoutId
    ? `Chargeback #${group.checkoutId} - Won`
    : 'Chargeback - Won';
};

const getDeductionLabel = (group: ChargebackAdjustmentGroup) => {
  const lostRow = group.negativeRows.find((row) => row.label?.includes('Lost'));
  const newRow = group.negativeRows.find((row) => row.label?.includes('New'));
  const fallbackRow = group.negativeRows[0];

  if (lostRow?.label) {
    return lostRow.label;
  }
  if (newRow?.label) {
    return newRow.label;
  }
  if (fallbackRow?.label) {
    return fallbackRow.label;
  }

  return group.checkoutId
    ? `Chargeback #${group.checkoutId} - New`
    : 'Chargeback - New';
};

export const buildDisplayChargebackAdjustments = (
  adjustments: Adjustment[] = []
): DisplayChargebackAdjustment[] => {
  const groups = adjustments.reduce<Record<string, ChargebackAdjustmentGroup>>(
    (acc, adjustment, index) => {
      const amount = Number(adjustment.amount) || 0;
      const key = getChargebackGroupKey(adjustment, index);

      if (!acc[key]) {
        acc[key] = {
          key,
          positiveTotal: 0,
          negativeTotal: 0,
          positiveRows: [],
          negativeRows: [],
          sortValue: getChargebackSortValue(adjustment),
          checkoutId:
            adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label),
        };
      }

      if (amount > 0) {
        acc[key].positiveTotal += amount;
        acc[key].positiveRows.push(adjustment);
      } else if (amount < 0) {
        acc[key].negativeTotal += amount;
        acc[key].negativeRows.push(adjustment);
      }

      return acc;
    },
    {}
  );

  return Object.values(groups)
    .reduce<SortableDisplayChargebackAdjustment[]>((acc, group) => {
      const amount = Number(
        (group.positiveTotal + group.negativeTotal).toFixed(2)
      );

      if (Math.abs(amount) < 0.005) {
        return acc;
      }

      acc.push({
        amount,
        key: group.key,
        sortValue: group.sortValue,
        checkoutId: group.checkoutId,
        label: amount > 0 ? getWonLabel(group) : getDeductionLabel(group),
      });

      return acc;
    }, [])
    .sort((a, b) => {
      if (Math.sign(a.amount) !== Math.sign(b.amount)) {
        return b.amount - a.amount;
      }

      if (typeof a.sortValue === 'number' && typeof b.sortValue === 'number') {
        return a.sortValue - b.sortValue;
      }

      return String(a.sortValue).localeCompare(String(b.sortValue));
    })
    .map((adjustment) => ({
      key: adjustment.key,
      label: adjustment.label,
      amount: adjustment.amount,
      checkoutId: adjustment.checkoutId,
    }));
};

export const minusTypes: KeysPaycheck[] = [
  'refundTotal',
  'advancesTotal',
  'chargebackTotal',
  'freeShippingTotal',
  'miscNegativeTotal',
];

export const boldTypes: KeysPaycheck[] = ['totalPay', 'totalPayout'];

export const getPayoutPaymentMethodOptions = (
  paymentMethods?: PaymentMethod[]
) =>
  paymentMethods
    ?.filter(({ id, name }) => {
      const label = name?.trim();

      return Boolean(id && label && !/^\d+$/.test(label));
    })
    .map(({ id, name }) => ({
      value: id,
      label: name.trim(),
    })) ?? [];

const signedAmountTypes = [
  PayoutType.Misc,
  PayoutType.Earnings,
  PayoutType.Adjustment,
  PayoutType.NegativeTip,
  PayoutType.NegativeEarnings,
  PayoutType.NegativeAdjustment,
];

const minusPayoutTypes = [
  PayoutType.Refund,
  PayoutType.Advance,
  PayoutType.NegativeTip,
  PayoutType.FreeShipping,
  PayoutType.PrepaidAdvance,
  PayoutType.NegativeEarnings,
  PayoutType.NegativeAdjustment,
];

export const getPayoutItemSignedAmount = (
  item: Pick<Payout, 'type' | 'amount'>
) => {
  const amount = Number(item.amount) || 0;

  if (signedAmountTypes.includes(item.type)) {
    return amount;
  }

  if (minusPayoutTypes.includes(item.type)) {
    return -Math.abs(amount);
  }

  return Math.abs(amount);
};

export const getSelectedPayoutTotal = (
  items: Payout[],
  selectedAmounts: Record<number, number>
) =>
  items.reduce((sum, item) => {
    const amount = selectedAmounts[item.id] || 0;
    const sign = getPayoutItemSignedAmount(item) < 0 ? -1 : 1;

    return sum + amount * sign;
  }, 0);

export const getFullPayoutItemAmount = (item: Payout) =>
  Math.abs(getPayoutItemSignedAmount(item));

const roundMoney = (value: number) => Number(value.toFixed(2));

const advanceTypes = [PayoutType.Advance, PayoutType.PrepaidAdvance];

const isAdvanceItem = (item: Payout) => advanceTypes.includes(item.type);

const itemTime = (item: Payout) =>
  new Date(item.date || item.createdAt || 0).getTime() || 0;

// A $0.00 row has no amount to send; its id alone pays it in full.
const fullRequest = (item: Payout) => {
  const amount = getFullPayoutItemAmount(item);
  return amount > 0 ? { amount, id: item.id } : { id: item.id };
};

// FINAL-FIX-PLAN F6: the line the backend makes to take an advance off a
// paycheck (clix-shared/services/advanceRepayment.js ADVANCE_REPAYMENT_NOTE).
// Only these carry; an older advance row is still taken whole, as before.
const isRepaymentLine = (item: Payout) =>
  isAdvanceItem(item) &&
  String(item.notes ?? '').startsWith('Advance repayment for #');

const signedSum = (items: Payout[]) =>
  roundMoney(
    items.reduce((sum, item) => sum + getPayoutItemSignedAmount(item), 0)
  );

/**
 * Advance repayment the pay left cannot hold (FINAL-FIX-PLAN F6, owner Q1):
 * advances come off what is left after the show's own pay and deductions, the
 * rest carries to the rep's next paycheck. 0 when everything fits, or when the
 * show's own deductions are already more than its pay (that stays blocked).
 */
export const getAdvanceCarry = (items: Payout[] = []) => {
  const payLeft = signedSum(items.filter((item) => !isRepaymentLine(item)));
  const advances = roundMoney(
    items
      .filter(isRepaymentLine)
      .reduce((sum, item) => sum + getFullPayoutItemAmount(item), 0)
  );
  return payLeft >= 0 && advances > payLeft
    ? roundMoney(advances - payLeft)
    : 0;
};

/**
 * The rows a payment of `payoutAmount` takes (FINAL-FIX-PLAN F5, owner
 * 23 Oct).
 * - Full amount, including a paycheck that comes to exactly $0: every row.
 * - Partial amount: every refund / deduction of the show first, then advances
 *   while they fit, then pay oldest first, the last pay row split to the cent.
 *   A partial payment never leaves deductions behind.
 */
export const getPayoutItemsForAmount = (
  items: Payout[] = [],
  payoutAmount: number,
  fullPayoutAmount: number
): PayrollPayoutItemRequest[] => {
  const amount = roundMoney(Math.max(Number(payoutAmount) || 0, 0));
  const fullAmount = roundMoney(Math.max(Number(fullPayoutAmount) || 0, 0));

  if (amount >= fullAmount) {
    if (!getAdvanceCarry(items)) {
      return items.map(fullRequest);
    }
    // F6: every other row, then repayment lines up to the pay left (the last
    // one split); the rest carries to the next paycheck.
    let payLeft = signedSum(items.filter((item) => !isRepaymentLine(item)));
    const taken = items
      .filter((item) => !isRepaymentLine(item))
      .map(fullRequest);
    items.filter(isRepaymentLine).forEach((item) => {
      const piece = roundMoney(
        Math.min(getFullPayoutItemAmount(item), payLeft)
      );
      if (piece > 0) {
        taken.push({ id: item.id, amount: piece });
        payLeft = roundMoney(payLeft - piece);
      }
    });
    return taken;
  }

  const deductions = items.filter(
    (item) => !isAdvanceItem(item) && getPayoutItemSignedAmount(item) < 0
  );
  const advances = items.filter(isAdvanceItem);
  const pay = items
    .filter((item) => getPayoutItemSignedAmount(item) > 0)
    .sort((a, b) => itemTime(a) - itemTime(b));

  const payAvailable = pay.reduce(
    (sum, item) => sum + getPayoutItemSignedAmount(item),
    0
  );
  const deductionTotal = deductions.reduce(
    (sum, item) => sum + Math.abs(getPayoutItemSignedAmount(item)),
    0
  );

  const result: PayrollPayoutItemRequest[] = deductions.map(fullRequest);

  // Advances come off while the pay left can still reach the typed amount.
  let advanceRoom = roundMoney(payAvailable - deductionTotal - amount);
  let advanceTotal = 0;
  advances.forEach((item) => {
    const advance = getFullPayoutItemAmount(item);
    if (advance > 0 && advance <= advanceRoom) {
      result.push({ id: item.id, amount: advance });
      advanceRoom = roundMoney(advanceRoom - advance);
      advanceTotal += advance;
    }
  });

  let payNeeded = roundMoney(amount + deductionTotal + advanceTotal);
  pay.forEach((item) => {
    if (payNeeded <= 0) {
      return;
    }
    const itemAmount = Math.min(getPayoutItemSignedAmount(item), payNeeded);
    payNeeded = roundMoney(payNeeded - itemAmount);
    result.push({ id: item.id, amount: roundMoney(itemAmount) });
  });

  return result;
};
